import { KnownTypes } from "@emed-labs/ftf-datatypes/lib/SessionData/KnownTypes";
import { Collection } from "@emed-labs/resource-api-models";
import { Method, RequestConfig } from "@emed-labs/platform-utility-generic-clients";
import { SessionMetadata, SessionRecord } from "@emed-labs/virtual-lab-models";
import { forwardRef, useContext, useImperativeHandle } from "react";
import { AuthContext } from "../AuthProvider";
import { VLabAPIContext } from "../VLabAPIProvider";
import { SessionDataPlugin, SessionDataPluginImperativeHandle } from "../SessionDataProvider";
import { LanguageContext } from "../LanguageProvider";

export const VAASPlugin = forwardRef<SessionDataPluginImperativeHandle, SessionDataPlugin>((props, ref) => {
	const VLabAPICtx = useContext(VLabAPIContext);
	const AuthCtx = useContext(AuthContext);
	const Language = useContext(LanguageContext);

	useImperativeHandle(ref, () => {
		return { callAPI, saveSessionData, getSessionData, getSessionMetadata, getLatestSessionData, isCompleted };
	});

	const callAPI = <Request, Response>(
		method: Method,
		endpoint: string,
		config?: RequestConfig<Request>
	): Promise<Response> => {
		config = config || {};
		config.headers = config.headers || {};
		config.headers["x-api-key"] = AuthCtx.token;
		config.headers["accept-language"] = Language.language;
		return VLabAPICtx.call<Request, Response>(method, endpoint, config);
	};

	const saveSessionData = <Record = SessionRecord,>(
		type: KnownTypes | string,
		data: Record,
		config?: RequestConfig
	) => {
		return callAPI<SessionRecord, void>("POST", `/v1/session/data/${AuthCtx.sessionID}`, {
			data: {
				sessionId: AuthCtx.sessionID,
				record: {
					type,
					data
				}
			}
		});
	};

	const getSessionData = <Record = SessionRecord,>(dataType: string, config?: RequestConfig) => {
		if (!config?.params?.limit) {
			config = config || {};
			config.params = config.params || {};
			/**
			 * these arent indexed, so need a greedier limit.
			 * TODO: while loop loading of all with nextStartKey,
			 * in case there would be records past the limit
			 */
			config.params.limit = 1000;
		}
		return callAPI<any, Collection<Record>>("GET", `/v1/session/data/${AuthCtx.sessionID}/${dataType}`, config);
	};
	const getLatestSessionData = <Record = SessionRecord,>(dataType: string) => {
		return callAPI<any, Record>("GET", `/v1/session/data/${AuthCtx.sessionID}/${dataType}/$latest`);
	};

	const getSessionMetadata = () => {
		return getLatestSessionData<SessionMetadata>("SessionMetadata");
	};

	const isCompleted = async () => {
		return await callAPI<void, boolean>("GET", `/v1/session/data/${AuthCtx.sessionID}/$completed`);
	};

	return props.children;
});
export default VAASPlugin;
